'use client'

import React, { forwardRef } from 'react'
import { Box, type BoxProps } from '@mantine/core'
import { useBreakpoint } from '@/hooks/useBreakpoints'

/**
 * Wraps content to break out of container and span full viewport width.
 * @param constrained - When true (e.g. full-bleed widgets): adds maw 1440, centered, padding like WidgetGenAi.
 *                      When false (default, e.g. carousels/sliders): breakout only, cards can bleed full width.
 */
interface FullBleedWrapperProps extends BoxProps {
    children: React.ReactNode
    /** When true, constrains content to maw 1440 + padding on desktop. When false, breakout only for full-width bleed. */
    constrained?: boolean
}

export const FullBleedWrapper = forwardRef<HTMLDivElement, FullBleedWrapperProps>(function FullBleedWrapper(
    { children, constrained = false, ...rest },
    ref,
) {
    const { isSm } = useBreakpoint()

    const inner =
        constrained && !isSm ? (
            <Box maw={1440} mx="auto" w="100%" px={{ base: 24, sm: 24 }}>
                {children}
            </Box>
        ) : (
            children
        )

    return (
        <Box
            ref={ref}
            w="100vw"
            style={{
                marginLeft: 'calc(50% - 50vw)',
                marginRight: 'calc(50% - 50vw)',
            }}
            {...rest}
        >
            {inner}
        </Box>
    )
})
